import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import EventInfo from "../components/EventInfo";
import StatusCard from "../components/StatusCard";
import {
    getCodexFromLocation,
    useLiveData,
    heatHasLiveRunner,
    pickAutoHeat,
    excelSerialToDateString,
    sexLabel,
    matchesCategory,
} from "../timeserver/uselivedata";

function startlistStatusLabel(s) {
    if (s.final_time != null) return `Classé${s.rank ? " (" + s.rank + "e)" : ""}`;
    if (s.status && s.status !== "OK") return s.status;
    if (s.start_status === "STARTED") return "En piste";
    return "AT START";
}

export default function StartlistPage() {
    const codex = getCodexFromLocation();
    const { data } = useLiveData(codex);
    const heats = useMemo(() => data.heats || {}, [data.heats]);
    const parameters = useMemo(() => data.parameters || {}, [data.parameters]);

    const [config, setConfig] = useState({});
    useEffect(() => {
        if (!codex) return;
        fetch(`/api/config/${encodeURIComponent(codex)}`)
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => setConfig(cfg || {}))
            .catch(() => {});
    }, [codex]);

    const [selectedHeat, setSelectedHeat] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null); // null = Toutes
    const isMobile = useMediaQuery("(max-width: 900px)");

    const categories = config.categories || [];
    const selectedCategory = categories.find((c) => c.id === selectedCategoryId) || null;

    const heatKeys = useMemo(
        () => Object.keys(heats).sort((a, b) => Number(a) - Number(b)),
        [heats]
    );

    const effectiveHeat =
        selectedHeat !== null && heats[selectedHeat] ? selectedHeat : pickAutoHeat(heats);
    const rawStartlist = effectiveHeat ? heats[effectiveHeat]?.startlist || [] : [];
    const startlist = selectedCategory
        ? rawStartlist.filter((s) => matchesCategory(s.year, s.sex, selectedCategory))
        : rawStartlist;

    const startlistRows = startlist.map((s) => ({
        id: s.bib,
        line: s.line,
        bib: s.bib,
        lastName: s.last_name,
        firstName: s.first_name,
        sex: sexLabel(s.sex),
        nation: s.nation,
        club: s.club,
        yearOfBirth: s.year,
        status: startlistStatusLabel(s),
    }));

    const startlistColumns = [
        { field: "line", headerName: "Ordre", flex: 0.4, minWidth: 60 },
        { field: "bib", headerName: "Dossard", flex: 0.6, minWidth: 70 },
        { field: "lastName", headerName: "Nom", flex: 1, minWidth: 100 },
        { field: "firstName", headerName: "Prénom", flex: 1, minWidth: 100 },
        { field: "sex", headerName: "Sexe", flex: 0.5, minWidth: 70 },
        { field: "nation", headerName: "Nation", flex: 0.7, minWidth: 70 },
        { field: "club", headerName: "Club", flex: 1, minWidth: 120 },
        { field: "yearOfBirth", headerName: "Année", flex: 0.6, minWidth: 80 },
        { field: "status", headerName: "Statut", flex: 0.9, minWidth: 130 },
    ];

    const gridStyles = {
        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#022a5b",
            borderBottom: "3px solid #f1242e",
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#022a5b",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            color: "#ffffff",
            fontWeight: 600,
        },
        "& .MuiDataGrid-columnSeparator": {
            color: "#ffffff",
        },
        "& .MuiDataGrid-cell": { borderBottom: "1px solid #c2c9cd", color: "#022a5b" },
        "& .MuiDataGrid-row:hover": { backgroundColor: "#f1242e", color: "#ffffff" },
    };

    const eventInfo = {
        name:
            config.eventTitle ||
            [parameters.TITLE1, parameters.TITLE2].filter(Boolean).join(" — ") ||
            "Événement",
        date: config.eventDate || excelSerialToDateString(parameters.DATE),
        location: parameters.LOCATION || "",
        logoLeft: config.logoLeft || "",
        logoRight: config.logoRight || "",
        showTicker: false,
        tickerMessage: "",
    };

    return (
        <Box sx={{ p: 3 }}>
            <EventInfo event={eventInfo} />

            <Chip
                component={Link}
                to={codex ? `/live/${codex}` : "#"}
                clickable
                label="🏁 Voir live / résultats"
                sx={{ mb: 2, fontWeight: "bold" }}
            />

            <Typography
                variant="h4"
                sx={{
                    mb: 3,
                    color: "#022a5b",
                    borderBottom: "3px solid #f1242e",
                    display: "inline-block",
                    pb: 1,
                }}
            >
                Startlist
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {heatKeys.map((key) => (
                    <Chip
                        key={key}
                        label={`Manche ${key}` + (heatHasLiveRunner(heats[key]) ? " ● LIVE" : "")}
                        color={key === effectiveHeat ? "primary" : "default"}
                        onClick={() => setSelectedHeat(key)}
                        sx={{ fontWeight: "bold" }}
                    />
                ))}
                <Chip
                    label="🔴 Auto"
                    color={selectedHeat === null ? "primary" : "default"}
                    onClick={() => setSelectedHeat(null)}
                    sx={{ fontWeight: "bold" }}
                />
            </Box>

            {categories.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                    <Chip
                        label="Toutes catégories"
                        color={selectedCategoryId === null ? "primary" : "default"}
                        onClick={() => setSelectedCategoryId(null)}
                    />
                    {categories.map((cat) => (
                        <Chip
                            key={cat.id}
                            label={cat.name || "(sans nom)"}
                            color={selectedCategoryId === cat.id ? "primary" : "default"}
                            onClick={() => setSelectedCategoryId(cat.id)}
                        />
                    ))}
                </Box>
            )}

            {startlist.length === 0 ? (
                <Typography>Liste de départ non disponible pour cette manche.</Typography>
            ) : isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {startlistRows.map((row) => (
                        <StatusCard key={row.bib} competitor={row} compact />
                    ))}
                </Box>
            ) : (
                <DataGrid
                    autoHeight
                    rows={startlistRows}
                    columns={startlistColumns}
                    pageSize={100}
                    rowsPerPageOptions={[100]}
                    disableSelectionOnClick
                    rowHeight={36}
                    sx={{ width: "100%", ...gridStyles }}
                />
            )}
        </Box>
    );
}